import { type CountdownFormat, ONE_HOUR_IN_MS, ONE_MINUTE_IN_MS, formatCountdown, isEmptyCountdown } from "@cede/utils";
import { CAMPAIGN_STATUS } from "@/components/features/campaigns/types";

/**
 * Formats the span between two dates as a compact human string
 * ("2h 30m", "6h", "45m 10s", "30s"). Returns null when either bound is
 * missing or the span is non-positive. Shared by the campaign card variants
 * to render epoch and past-campaign durations.
 */
export function formatEpochDurationFromDates(start: Date | undefined, end: Date | undefined): string | null {
  if (!start || !end) return null;
  const totalMs = new Date(end).getTime() - new Date(start).getTime();
  if (totalMs <= 0) return null;

  const hours = Math.floor(totalMs / ONE_HOUR_IN_MS);
  const minutes = Math.floor((totalMs % ONE_HOUR_IN_MS) / ONE_MINUTE_IN_MS);
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;

  const seconds = Math.floor((totalMs % ONE_MINUTE_IN_MS) / 1000);
  return minutes > 0 ? `${minutes}m ${seconds > 0 ? `${seconds}s` : ""}`.trim() : `${seconds}s`;
}

export interface CampaignTimeInfo {
  /** Display label: "Ends", "Starts in", or "Status" */
  label: string;
  /** Formatted display value */
  value: string;
  /** Whether the campaign hasn't started yet (future or paused) — used for warning styling */
  isUpcoming: boolean;
}

/**
 * Derives time display info from campaign status and countdown.
 * Centralizes the status-based time logic used across campaign card components.
 */
export function getCampaignTimeInfo(
  status: string,
  countdown: CountdownFormat | null,
  options?: { pastDuration?: string | null },
): CampaignTimeInfo {
  const formatted = countdown ? formatCountdown(countdown, "compact-smart-pair") : null;

  switch (status) {
    case CAMPAIGN_STATUS.LIVE:
      return { label: "Ends in", value: formatted ?? "", isUpcoming: false };
    case CAMPAIGN_STATUS.FUTURE: {
      const startingSoon = (countdown && isEmptyCountdown(countdown)) || formatted === "0s";
      return {
        label: "Starts in",
        value: startingSoon ? "Start soon" : formatted || "Soon",
        isUpcoming: true,
      };
    }
    case CAMPAIGN_STATUS.PAUSED:
      return { label: "Starts in", value: "Start soon", isUpcoming: true };
    default:
      return { label: "Ended", value: options?.pastDuration ?? "—", isUpcoming: false };
  }
}
